<template>
  <fullscreen-dialog
    v-model="model"
    :title="`${toLatin(employee?.name || '')} ${toLatin(employee?.lastname || '')}, ID ${
      employee?.id || ''
    }`"
    :subtitle="toLatin(company?.company || '')"
    hide-footer
  >
    <template #default>
      <v-card-text class="py-4">
        <v-row align="center">
          <v-col>
            <month-picker
              v-model="date"
              @updatedMonthDates="monthDates = $event"
              color="white"
            />
          </v-col>
          <v-col cols="auto">
            <text-field
              v-model="search"
              kind="search"
              :label="$t('search')"
              style="width: 250px"
              color="white"
            ></text-field>
          </v-col>
          <v-col cols="auto">
            <autocomplete
              v-model="selectedRestaurants"
              :items="restaurants"
              :label="$t('restaurants')"
              color="white"
              multiple
              chips
              small-chips
              :hide-details="false"
              style="min-width: 250px; max-width: 500px"
            ></autocomplete>
          </v-col>
          <v-col cols="auto">
            <v-checkbox
              class="show-all-checkbox mt-4"
              :label="$t('show_all_days')"
              v-model="showAllDates"
              dense
            />
          </v-col>
          <v-col cols="auto">
            <print-button @click="print"></print-button>
          </v-col>
          <v-col cols="auto">
            <dropdown
              :label="$t('view')"
              v-model="selectedLayout"
              :items="layouts"
              color="white"
              style="width: 280px"
            ></dropdown>
          </v-col>
        </v-row>
        <v-data-table
          :headers="headers"
          :items="filteredRows"
          :search="search"
          :items-per-page="-1"
          hide-default-footer
          class="ordera-box-shadow striped-table"
          :loading="loading"
        >
          <template v-slot:item.date="{ item }">
            {{ localeDate(item.date) }}
          </template>
          <template v-slot:item.price="{ item }">
            <span class="text-right d-block">{{ item.price.toFixed(2) }}</span>
          </template>
          <template v-slot:item.credits_spent="{ item }">
            <span class="text-right d-block">{{ item.credits_spent.toFixed(2) }}</span>
          </template>
          <template v-slot:item.meal_name="{ item }">
            <v-tooltip bottom v-if="item.order_id">
              <template v-slot:activator="{ on, attrs }">
                <span v-bind="attrs" v-on="on">
                  <span v-if="selectedLayout === 0"
                    ><strong>{{ item.quantity }}x</strong> {{ item.meal_name }}</span
                  >
                  <span v-else-if="selectedLayout === 1">{{ item.meal_name }}</span>
                  <span v-else-if="selectedLayout === 2"
                    ><strong>{{ item.quantity }}</strong></span
                  >
                </span>
              </template>
              <span>#{{ item.order_id }}</span>
            </v-tooltip>
            <template v-else>
              <span v-if="selectedLayout === 0"
                ><strong>{{ item.quantity }}x</strong> {{ item.meal_name }}</span
              >
              <span v-else-if="selectedLayout === 1">{{ item.meal_name }}</span>
              <span v-else-if="selectedLayout === 2"
                ><strong>{{ item.quantity }}</strong></span
              >
            </template>
          </template>
          <template v-slot:body.append>
            <!-- layout 0: Date, Shift, Restaurant, Meal, Ordered, Taken, Difference, Price, Credits -->
            <tr v-if="selectedLayout === 0">
              <td style="font-weight: bold" colspan="4">{{ $t("total") }}</td>
              <td style="font-weight: bold" class="text-center">{{ totalQuantity }}</td>
              <td style="font-weight: bold" class="text-center">{{ totalTaken }}</td>
              <td style="font-weight: bold" class="text-center">{{ totalDifference }}</td>
              <template v-if="company?.is_credit">
                <td style="font-weight: bold" class="text-right">{{ totalPrice }}</td>
                <td style="font-weight: bold" class="text-right">
                  {{ totalCreditsSpent }}
                </td>
              </template>
            </tr>
            <!-- layout 1: Date, Shift, Restaurant, Meal, Price, Credits (no quantity cols) -->
            <tr v-else-if="selectedLayout === 1">
              <td style="font-weight: bold" colspan="4">{{ $t("total") }}</td>
              <template v-if="company?.is_credit">
                <td style="font-weight: bold" class="text-right">{{ totalPrice }}</td>
                <td style="font-weight: bold" class="text-right">
                  {{ totalCreditsSpent }}
                </td>
              </template>
            </tr>
            <!-- layout 2: Date, Shift, Restaurant, Ordered, Taken, Difference, Price, Credits (no meal col) -->
            <tr v-else-if="selectedLayout === 2">
              <td style="font-weight: bold" colspan="3">{{ $t("total") }}</td>
              <td style="font-weight: bold" class="text-center">{{ totalQuantity }}</td>
              <td style="font-weight: bold" class="text-center">{{ totalTaken }}</td>
              <td style="font-weight: bold" class="text-center">{{ totalDifference }}</td>
              <template v-if="company?.is_credit">
                <td style="font-weight: bold" class="text-right">{{ totalPrice }}</td>
                <td style="font-weight: bold" class="text-right">
                  {{ totalCreditsSpent }}
                </td>
              </template>
            </tr>
          </template>
        </v-data-table>
      </v-card-text>
    </template>
  </fullscreen-dialog>
</template>

<script>
import reportService from "@/services/report";
import hasModel from "@/mixins/hasModel";
import hasPrinting from "@/mixins/hasPrinting";
import hasSnackbar from "@/mixins/hasSnackbar";
import FullscreenDialog from "@/components/dialogs/FullscreenDialog.vue";

export default {
  name: "ReportEmployeeOrderHistoryDialogMoreRestaurants",
  mixins: [hasModel, hasPrinting, hasSnackbar],
  components: { FullscreenDialog },
  props: {
    employee: { type: Object, default: () => ({}) },
    company: { type: Object, default: () => ({}) },
  },
  data() {
    return {
      loading: false,
      date: new Date().toISOString().substr(0, 7),
      monthDates: [],
      search: "",
      rawData: [],
      selectedLayout: 0,
      selectedRestaurants: [],
      showAllDates: false,
    };
  },
  created() {
    const dateTo = this.$store.state.report?.filters?.dates?.to;
    if (dateTo) {
      const d = new Date(dateTo);
      this.date = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
    }
  },
  computed: {
    month() {
      return parseInt(this.date.split("-")[1]);
    },
    year() {
      return parseInt(this.date.split("-")[0]);
    },
    monthStart() {
      return `${this.date}-01`;
    },
    monthEnd() {
      const lastDay = new Date(this.year, this.month, 0).getDate();
      return `${this.date}-${String(lastDay).padStart(2, "0")}`;
    },
    layouts() {
      return [
        { text: this.$t("quantities_and_names"), value: 0 },
        { text: this.$t("names_only"), value: 1 },
        { text: this.$t("quantities_only"), value: 2 },
      ];
    },
    headers() {
      const base = [
        { text: this.$t("date"), value: "date" },
        { text: this.$t("shift.shift"), value: "shift" },
        { text: this.$t("restaurant"), value: "restaurant_name" },
      ];
      // layout 0 = quantities_and_names, 1 = names_only, 2 = quantities_only
      if (this.selectedLayout !== 2) {
        base.push({ text: this.$t("meal"), value: "meal_name" });
      }
      if (this.selectedLayout !== 1) {
        base.push({ text: this.$t("ordered"), value: "quantity", align: "center" });
        base.push({
          text: this.$t("taken"),
          value: "quantity_payed",
          align: "center",
        });
        base.push({
          text: this.$t("difference"),
          value: "difference",
          align: "center",
        });
      }
      if (this.company?.is_credit) {
        base.push(
          {
            text: this.$t("price") + ` (${this.majorityRestaurantCurrency})`,
            value: "price",
            align: "right",
          },
          {
            text: this.$t("spent_on_credits") + ` (${this.majorityRestaurantCurrency})`,
            value: "credits_spent",
            align: "right",
          }
        );
      }
      return base;
    },
    restaurants() {
      return [
        ...new Set(this.allRows.map((r) => r.restaurant_name).filter(Boolean)),
      ].sort();
    },
    allRows() {
      return this.rawData.map((item, i) => {
        const currency = this.majorityRestaurantCurrency;
        return {
          num: i + 1,
          date: item.date,
          shift: item.shift,
          restaurant_name: this.toLatin(item.restaurant_name || ""),
          meal_name: this.toLatin(item.meal_name || item.food_name || ""),
          order_id: item.order_id,
          quantity: item.quantity || 0,
          quantity_payed: item.quantity_payed || 0,
          difference: (item.quantity || 0) - (item.quantity_payed || 0),
          price: parseFloat(item.price) || 0,
          credits_spent: parseFloat(item.quantity_payed) || 0,
          currency,
        };
      });
    },
    filteredRows() {
      let rows = this.allRows;
      if (this.selectedRestaurants.length) {
        rows = rows.filter((r) => this.selectedRestaurants.includes(r.restaurant_name));
      }
      if (!this.showAllDates) return rows;
      // When showAllDates is on, insert empty placeholder rows for days with no orders
      const datesWithOrders = new Set(rows.map((r) => r.date));
      const emptyDays = this.monthDates
        .filter((d) => !datesWithOrders.has(d))
        .map((d) => ({
          date: d,
          shift: null,
          restaurant_name: "",
          meal_name: "",
          quantity: 0,
          quantity_payed: 0,
          difference: 0,
          price: 0,
          credits_spent: 0,
          currency: "",
        }));
      return [...rows, ...emptyDays].sort((a, b) => (a.date > b.date ? 1 : -1));
    },
    totalQuantity() {
      return this.filteredRows.reduce((sum, r) => sum + r.quantity, 0);
    },
    totalTaken() {
      return this.filteredRows.reduce((sum, r) => sum + r.quantity_payed, 0);
    },
    totalDifference() {
      return this.totalQuantity - this.totalTaken;
    },
    totalPrice() {
      return this.filteredRows.reduce((sum, r) => sum + r.price, 0).toFixed(2);
    },
    totalCreditsSpent() {
      return this.filteredRows.reduce((sum, r) => sum + r.credits_spent, 0).toFixed(2);
    },
  },
  methods: {
    print() {
      const printItems = this.filteredRows.map((r) => ({
        ...r,
        date: this.localeDate(r.date),
      }));
      printItems.push({
        date: this.$t("total"),
        shift: "",
        restaurant_name: "",
        meal_name: "",
        quantity: this.totalQuantity,
        quantity_payed: this.totalTaken,
        difference: this.totalDifference,
        price: this.totalPrice,
        credits_spent: this.totalCreditsSpent,
      });
      this.printDocument({
        tables: [{ headers: this.headers, items: printItems }],
        title: `${this.toLatin(this.employee.name)} ${this.toLatin(
          this.employee.lastname
        )}`,
        subtitle: [this.monthDates[0], this.monthDates.slice(-1)[0]]
          .filter(Boolean)
          .map((d) => this.localeDate(d))
          .join(" - "),
      });
    },
    async loadReport() {
      if (!this.employee?.id) return;
      this.loading = true;
      this.rawData = [];
      try {
        const {
          data,
        } = await reportService.getEmployeeOrdersByMonthCustom(
          "/user/more-restaurants-orders-by-month",
          {
            month: this.month,
            year: this.year,
            date_from: this.monthStart,
            date_to: this.monthEnd,
            user_id: this.employee.id,
          }
        );
        this.rawData = Array.isArray(data) ? data : [];
      } catch (error) {
        this.showErrorSnackbar(error, this.$t("error_loading_report"));
      } finally {
        this.loading = false;
      }
    },
  },
  watch: {
    employee: {
      handler(val) {
        if (val) this.loadReport();
      },
      immediate: true,
    },
    date() {
      this.loadReport();
    },
    restaurants: {
      handler(val) {
        this.selectedRestaurants = [...val];
      },
      immediate: true,
    },
  },
};
</script>

<style scoped>
.v-data-table >>> tr:nth-of-type(odd) {
  background-color: rgba(0, 0, 0, 0.05) !important;
}
div.v-select__selections span.v-size--small span.v-chip__content {
  font-size: 10px !important;
}
</style>
